// frontend/app/components/lessonPlans/present/usePresentKeys.js
//
// A presentation remote must just work, with no pairing screen and no settings.
// That is achievable because clickers do not speak a protocol — they enumerate
// as a USB keyboard and send exactly the keys this hook binds: Page Up / Page
// Down on most of them (Logitech, Kensington), Left / Right on the rest. Bind
// both and every clicker in a school cupboard already works.
//
// Space and Enter are the "next beat" key: reveal the answer, or if there is
// nothing left to reveal, move on. That is one button doing the obvious thing,
// which is the only thing a teacher can operate without looking down.
//
// N opens the teacher's notes, S opens Saras, P opens the presenter window on
// the second screen. All three are letters no clicker will ever send by
// accident, which is the whole reason they are letters.
//
// P has one property the other two do not: a pop-up blocker only lets
// window.open through while the gesture that asked for it is still on the
// stack, and a keydown IS that gesture — so the callback must open the window
// itself and never defer into an effect. See usePresenterWindow.open.

import { useEffect } from "react";

const PREVIOUS_KEYS = ["ArrowLeft", "PageUp"];
const NEXT_KEYS = ["ArrowRight", "PageDown"];
// "Spacebar" is the legacy IE/Edge spelling; some clicker drivers still emit
// it, and it costs one array entry to keep those working.
const ADVANCE_KEYS = [" ", "Spacebar", "Enter"];

const TEXT_ENTRY_TAGS = ["INPUT", "TEXTAREA", "SELECT"];

/**
 * Is the caret in a field the teacher is typing into?
 *
 * The Saras panel is the first thing in present mode a teacher types into, and
 * it is what makes this its own check: inside a text field the arrow keys move
 * the caret. Without this, a teacher typing a question and pressing Left to fix
 * a typo changes the slide behind the panel.
 */
const isTextEntryTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toUpperCase();
  if (TEXT_ENTRY_TAGS.includes(tag)) return true;
  return !!target.isContentEditable;
};

/**
 * Is the keypress already spoken for by whatever has focus?
 *
 * React Native Web renders every Touchable as a focusable `div[role=button]`
 * that answers Space and Enter itself. Without this check, pressing Space while
 * the Reveal button has focus fires the button AND this hook, and the deck
 * jumps two beats on one press. Arrow keys are not affected by a button — it
 * ignores them — so they consult isTextEntryTarget instead.
 */
const isInteractiveTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  if (isTextEntryTarget(target)) return true;
  const tag = String(target.tagName || "").toUpperCase();
  const role =
    typeof target.getAttribute === "function" ? target.getAttribute("role") : null;
  return tag === "BUTTON" || tag === "A" || role === "button" || role === "link";
};

/**
 * All callbacks are optional; a missing one simply means that key does nothing.
 *
 * `onCloseSaras` is the exception that carries meaning by its presence: pass it
 * ONLY while the Saras panel is open. Escape then closes the panel and never
 * reaches onExit, which is the whole point — a teacher dismissing an answer in
 * front of a class must not be dropped out of their lesson. Expressing that as
 * "is this callback here" rather than as a boolean flag means the precedence
 * cannot go stale against the panel's actual state.
 *
 * The environment is feature-detected rather than gated on Platform.OS: React
 * Native defines a `window` global but no addEventListener, so this is both the
 * accurate test and the one that keeps the hook inert on a phone.
 */
const usePresentKeys = ({
  onPrevious,
  onNext,
  onAdvance,
  onExit,
  onToggleNotes,
  onToggleSaras,
  onTogglePresenter,
  onCloseSaras,
  enabled = true,
}) => {
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    if (typeof window.addEventListener !== "function") return undefined;

    const onKeyDown = (event) => {
      // Someone nearer the event already handled it — a modal's own Escape,
      // say. Present mode does not get to override that.
      if (event.defaultPrevented) return;
      // Leave every browser and OS shortcut alone: Ctrl+P, Cmd+Left, Alt+Tab.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;

      if (PREVIOUS_KEYS.includes(key)) {
        if (isTextEntryTarget(event.target)) return;
        // Without this the arrow both navigates and scrolls the slide, so a
        // long step appears to lurch as it changes.
        event.preventDefault();
        if (onPrevious) onPrevious();
        return;
      }

      if (NEXT_KEYS.includes(key)) {
        if (isTextEntryTarget(event.target)) return;
        event.preventDefault();
        if (onNext) onNext();
        return;
      }

      if (ADVANCE_KEYS.includes(key)) {
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        if (onAdvance) onAdvance();
        return;
      }

      if (key === "Escape") {
        event.preventDefault();
        // Escape dismisses the nearest thing first. With Saras open that is the
        // panel, and the lesson keeps running behind it.
        if (onCloseSaras) {
          onCloseSaras();
          return;
        }
        if (onExit) onExit();
        return;
      }

      // Notes are the one thing on this surface the room should not read by
      // default, so the key that shows them is deliberately a letter no clicker
      // will ever send by accident.
      if (key === "n" || key === "N") {
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        if (onToggleNotes) onToggleNotes();
        return;
      }

      // S for Saras — the second half of the pair a teacher learns as one
      // gesture. The interactive-target check is what lets them type "so why
      // does..." into the panel's own box without it closing under them.
      if (key === "s" || key === "S") {
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        if (onToggleSaras) onToggleSaras();
        return;
      }

      // P for the presenter window. Handled last because it is the rarest of
      // the three — pressed once at the start of a lesson, not during it.
      if (key === "p" || key === "P") {
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        if (onTogglePresenter) onTogglePresenter();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    enabled,
    onAdvance,
    onCloseSaras,
    onExit,
    onNext,
    onPrevious,
    onToggleNotes,
    onTogglePresenter,
    onToggleSaras,
  ]);
};

export default usePresentKeys;
